import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FileSpreadsheet, Loader2, X, Download } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full px-3 py-2 text-sm border border-zinc-300 outline-none focus:border-[#D4AF37]";
const TEMPLATE = "Sort_Order,Parameter,Code,Description\n1,Sensor Size,150,DN150 (6 in.)\n1,Sensor Size,200,DN200 (8 in.)\n2,Medium Type,L,Liquid\n2,Medium Type,G,Gas\n";

function guessCode(filename) {
  const all = [...(filename || "").matchAll(/([A-Za-z]{1,3})[-\s]?(\d{3,5})/g)];
  const m = all[all.length - 1];
  return m ? `${m[1].toUpperCase()}-${m[2]}` : "";
}

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "code_builder_template.csv"; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ImportProductModal({ products, current, onClose, onDone }) {
  const [mode, setMode] = useState("new");
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [target, setTarget] = useState(current?.id || products[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const tgt = products.find((p) => p.id === target);

  const pick = (f) => { setFile(f); if (f && !code) setCode(guessCode(f.name)); };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a spreadsheet first");
    if (mode === "replace" && !window.confirm(`Replace ALL fields and options of ${tgt?.name}? Saved customer models keep their model numbers.`)) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("mode", mode);
    if (mode === "replace") fd.append("product_id", target); else { fd.append("name", name); fd.append("code", code); }
    try {
      const { data } = await api.post("/inst/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = data.summary;
      toast.success(`${data.product.name}: ${s.fields} fields, ${s.options} options imported`);
      onDone(data.product.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="inst-import-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg">
        <div className="bg-[#0F172A] text-white px-5 py-4 flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-[#D4AF37]" /> Import code-builder spreadsheet</h3>
          <button type="button" onClick={onClose} data-testid="inst-import-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 border border-zinc-300">
            {[["new", "Create new product"], ["replace", "Update existing product"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setMode(k)} data-testid={`inst-import-mode-${k}`}
                className={`py-2 text-sm ${mode === k ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>{l}</button>
            ))}
          </div>
          <div>
            <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid="inst-import-file" />
            <button type="button" onClick={() => ref.current?.click()} data-testid="inst-import-choose"
              className="w-full border border-dashed border-zinc-400 py-4 text-sm text-zinc-600 hover:border-[#D4AF37]">
              {file ? <span className="font-medium text-[#0F172A]">{file.name}</span> : "Choose .xlsx or .csv file"}
            </button>
            <p className="mt-1.5 text-xs text-zinc-500">Columns: Sort_Order, Parameter, Code, Description — one row per choice, same layout as the F-1001 code builder.
              <button type="button" onClick={downloadTemplate} data-testid="inst-import-template" className="ml-1 inline-flex items-center gap-1 text-[#0F172A] underline"><Download className="h-3 w-3" />Template</button></p>
          </div>
          {mode === "new" ? (
            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-2 text-xs text-zinc-500">Product name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. PI-5020 Magnetic Flow Meter" data-testid="inst-import-name" className={`${inp} mt-1`} /></label>
              <label className="text-xs text-zinc-500">Code<input value={code} onChange={(e) => setCode(e.target.value)} placeholder="PI-5020" data-testid="inst-import-code" className={`${inp} mt-1 font-mono`} /></label>
            </div>
          ) : (
            <label className="block text-xs text-zinc-500">Product to update
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="inst-import-target" className={`${inp} mt-1`}>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <span className="block mt-1 text-[11px] text-zinc-400">Replaces all fields and choices. Name, description, photo and highlights stay the same.</span>
            </label>
          )}
          <button type="submit" disabled={busy} data-testid="inst-import-submit"
            className="w-full bg-[#D4AF37] text-[#0F172A] font-semibold py-2.5 text-sm hover:bg-[#c9a42f] disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} {mode === "new" ? "Import as new product" : "Update product options"}
          </button>
        </div>
      </form>
    </div>
  );
}
